import styles from './itinerarycard.module.scss';
import { ReactNode, useMemo, useState } from 'react';
import { Button, Image } from '@msc-b2c/shared-components';
import {
  addDays,
  AppDispatch,
  breakpoints,
  countDaysUntil,
  formatTime,
  getActiveSession,
  getBookingStatusConfig,
  getCurrencySymbol,
  managePlurals,
  negativeBalanceNoteRetrieved,
  replacePlaceholders,
  resolveDateFormat,
  RootState,
  triggerShowNegativeBalance,
} from '@msc-b2c/shared-utils';
import {
  GetShipsResponse,
  ItineraryCardWrapperProps,
  ItineraryResponse,
} from '@msc-b2c/interfaces/types';
import {
  AllBooking,
  BookingStatusObject,
  CabinI,
  GetBookingMyMscResponse,
  IGetExperienceDetailsResponse,
  ImageConfig,
} from '@msc-b2c/interfaces/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { triggerClickCtaEvent } from '../utils/myAreaAnalytics';
import { getContainerInfo } from '../utils/analyticsUtils';
import { getBookedExperienceDetail, isB2CBooking, isPaymentEnabled } from '../utils/myAreaUtils';
import dayjs from 'dayjs';
import { formatDateRangeForItinerary } from './ItineraryCard.utils';
import { BookingStatusEnum } from '@msc-b2c/interfaces/enums';

const ItineraryCard = ({
  bookingData,
  canvasItineraryName,
  itineraryLabels,
  itineraryData,
  shipData,
  imageData,
  isAllMyCruises,
  allMyCruisesHandleClick,
  experiences,
  disableRemainingBalanceB2B,
}: {
  bookingData: GetBookingMyMscResponse;
  canvasItineraryName: string;
  itineraryLabels: ItineraryCardWrapperProps;
  itineraryData: ItineraryResponse;
  shipData: GetShipsResponse;
  imageData?: ImageConfig;
  isAllMyCruises: boolean;
  allMyCruisesHandleClick?: () => void;
  experiences?: IGetExperienceDetailsResponse;
  disableRemainingBalanceB2B: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  itineraryLabels.negativeBalanceNote &&
    dispatch(negativeBalanceNoteRetrieved(itineraryLabels.negativeBalanceNote));

  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const handleViewBookingDetails = () => {
    setIsDetailsOpen((prevState) => !prevState);
  };
  const renderStatus = () => {
    if (bookingData?.status.code === BookingStatusEnum.OPTIONAL && isB2CBooking(bookingData)) {
      return (
        <CourtesyHoldBookingStatus
          bookingData={bookingData}
          itineraryLabels={itineraryLabels}
        />
      );
    } else {
      const totalPrice =
        Number(bookingData?.totalWithoutPrepaid?.due?.gross) > 0
          ? Number(bookingData?.totalWithoutPrepaid?.due?.gross)
          : Number(bookingData?.total?.due?.gross);
      if (totalPrice > 0) {
        return (
          <StandardBookingStatus
            bookingData={bookingData}
            itineraryLabels={itineraryLabels}
            disableRemainingBalanceB2B={disableRemainingBalanceB2B}
          />
        );
      } else if (totalPrice === 0) {
        return (
          <PaidInFullBookingStatus
            bookingData={bookingData}
            itineraryLabels={itineraryLabels}
          />
        );
      } else {
        itineraryLabels.negativeBalanceNote.enabled && dispatch(triggerShowNegativeBalance(true));
        return (
          <NegativeBookingStatus
            bookingData={bookingData}
            itineraryLabels={itineraryLabels}
          />
        );
      }
    }
  };

  if (bookingData) {
    const allMyCruisesSkeleton = document.querySelector('.cmp-allmycruises-skeleton');
    if (allMyCruisesSkeleton) {
      // If it exists, remove it from the DOM
      allMyCruisesSkeleton.remove();
    }
  }

  return (
    bookingData && (
      <div className={styles['cmp-itineraryCard-container']}>
        <div className={styles['cmp-itineraryCard-container-main']}>
          <BookingHero
            canvasItineraryName={canvasItineraryName}
            itineraryLabels={itineraryLabels}
            bookingData={bookingData}
            itineraryData={itineraryData}
            shipData={shipData}
            imageData={imageData}
          />
          {isAllMyCruises ? (
            <AllMyCruisesStatus
              bookingData={bookingData}
              itineraryLabels={itineraryLabels}
              isAllMyCruises={isAllMyCruises}
              allMyCruisesHandleClick={allMyCruisesHandleClick}
            />
          ) : (
            renderStatus()
          )}
        </div>
        <Button
          text={itineraryLabels.viewBookingDetailsLabel}
          onClick={handleViewBookingDetails}
          styleOption={'cmp-button__link -position-right -text-center'}
          icon={isDetailsOpen ? 'up' : 'down'}
        />
        {isDetailsOpen && (
          <BookingDetails
            bookingData={bookingData}
            itineraryLabels={itineraryLabels}
            itineraryData={itineraryData}
            shipData={shipData}
            isAllMyCruises={isAllMyCruises}
            experiences={experiences}
          />
        )}
      </div>
    )
  );
};

function callAnalytics(itineraryLabels: ItineraryCardWrapperProps) {
  const container = document.querySelector('.itinerarycard');

  triggerClickCtaEvent({
    cta_dest_link: itineraryLabels.itineraryButtonLink,
    click_main_name: itineraryLabels.itineraryButtonLabel,
    container_info: getContainerInfo(container),
    element_position: `itinerary card`,
    element_type: 'button',
    click_location: `fixed`,
  });
}

const StandardBookingStatus = ({
  bookingData,
  itineraryLabels,
  disableRemainingBalanceB2B,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
  disableRemainingBalanceB2B: disableRemainingBalanceB2B;
}) => {
  return (
    <BookingStatus>
      <BookingStatus.Balance
        balanceLabel={itineraryLabels.positiveBalanceLabel ?? ''}
        balanceDescription={(() => {
          if (isB2CBooking(bookingData)) {
            return itineraryLabels.positiveBalanceDescription;
          } else {
            return itineraryLabels.totalRemainingBalanceDescriptionB2B;
          }
        })()}
        bookingData={bookingData}
        disableRemainingBalanceB2B={disableRemainingBalanceB2B}
      />
      {isPaymentEnabled(bookingData) && (
        <BookingStatus.Button
          buttonLabel={itineraryLabels.itineraryButtonLabel}
          buttonLink={itineraryLabels.itineraryButtonLink}
          handleClick={() => {
            callAnalytics(itineraryLabels);
          }}
        />
      )}
    </BookingStatus>
  );
};

const NegativeBookingStatus = ({
  bookingData,
  itineraryLabels,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
}) => {
  return (
    <BookingStatus>
      <BookingStatus.Balance
        balanceLabel={itineraryLabels.negativeBalanceLabel ?? ''}
        balanceDescription={itineraryLabels.negativeBalanceDescription ?? ''}
        bookingData={bookingData}
      />
    </BookingStatus>
  );
};

const PaidInFullBookingStatus = ({
  bookingData,
  itineraryLabels,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
}) => {
  return (
    <BookingStatus>
      <BookingStatus.Balance
        balanceLabel={itineraryLabels.paidBalanceLabel ?? ''}
        balanceDescription={itineraryLabels.paidBalanceDescription ?? ''}
        bookingData={bookingData}
      />
    </BookingStatus>
  );
};

const AllMyCruisesStatus = ({
  bookingData,
  itineraryLabels,
  isAllMyCruises,
  allMyCruisesHandleClick,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
  isAllMyCruises: boolean;
  allMyCruisesHandleClick?: () => void;
}) => {
  return (
    <BookingStatus>
      <BookingStatus.Status
        statusLabel={itineraryLabels.statusLabel}
        bookingData={bookingData}
        isAllMyCruises={isAllMyCruises}
      />
      <BookingStatus.Button
        buttonLabel={itineraryLabels.itineraryButtonLabel}
        buttonLink={itineraryLabels.itineraryButtonLink}
        isRouting={isAllMyCruises}
        handleClick={allMyCruisesHandleClick}
      />
    </BookingStatus>
  );
};

const CourtesyHoldBookingStatus = ({
  bookingData,
  itineraryLabels,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
}) => {
  return (
    <BookingStatus>
      <BookingStatus.Balance
        balanceLabel={itineraryLabels.courtesyHoldBalanceLabel ?? ''}
        balanceDescription={(() => {
          return itineraryLabels.courtesyHoldBalanceDescription;
        })()}
        bookingData={bookingData}
      />
      {isPaymentEnabled(bookingData) && (
        <BookingStatus.Button
          buttonLabel={itineraryLabels.courtesyHoldBalanceButtonLabel}
          buttonLink={itineraryLabels.itineraryButtonLink}
          handleClick={() => {
            callAnalytics(itineraryLabels);
          }}
        />
      )}
    </BookingStatus>
  );
};

const BookingHero = ({
  canvasItineraryName,
  itineraryLabels,
  bookingData,
  itineraryData,
  shipData,
  imageData,
}: {
  canvasItineraryName: string;
  itineraryLabels: ItineraryCardWrapperProps;
  bookingData: GetBookingMyMscResponse;
  itineraryData: ItineraryResponse;
  shipData: GetShipsResponse;
  imageData: ImageConfig;
}) => {
  const daysUntil = useMemo(
    () => countDaysUntil(undefined, itineraryData.departureDate),
    [itineraryData.departureDate]
  );
  const cruiseFinishDate = useMemo(
    () => addDays(itineraryData.departureDate, itineraryData.numberOfSailingDays),
    [itineraryData.departureDate, itineraryData.numberOfSailingDays]
  );

  return (
    <div className={styles['cmp-itineraryCard-bookingHero__container']}>
      <div className={styles['cmp-itineraryCard-bookingHero__ribbon']}>
        <div className={styles['cmp-itineraryCard-bookingHero__ribbon-text']}>
          {itineraryLabels.youSetSailLabel}
        </div>
        <div className={styles['cmp-itineraryCard-bookingHero__ribbon-text-sub']}>
          {replacePlaceholders(itineraryLabels.youSetSailDaysLabel, {
            amountOfDays: daysUntil.toString(),
            dayField: managePlurals(daysUntil, itineraryLabels.dayFieldLabel),
          })}
        </div>
      </div>
      <div className={styles['cmp-itineraryCard-bookingHero__image']}>
        <Image
          alt={imageData?.altText ?? canvasItineraryName}
          src={imageData?.imagePath ?? imageData?.imgDmPath ?? ''}
          sizes={`(max-width: ${breakpoints.md}px) ${breakpoints.md}px, ${breakpoints.lg}px`}
        />
      </div>
      <div className={styles['cmp-itineraryCard-bookingHero__texts']}>
        <div className={styles['cmp-itineraryCard-bookingHero__texts-titles']}>
          <div className={styles['cmp-itineraryCard-bookingHero__texts-bookingTitleLabel']}>
            {replacePlaceholders(itineraryLabels.bookingTitleLabel, {
              location: canvasItineraryName,
              duration: Number(itineraryData.cruiseDuration).toString(),
              nightField: managePlurals(
                Number(itineraryData.numberOfSailingDays),
                itineraryLabels.nightFieldLabel
              ),
            })}
          </div>
          <div className={styles['cmp-itineraryCard-bookingHero__texts-bookingSubtitleLabel']}>
            {replacePlaceholders(itineraryLabels.bookingSubtitleLabel, {
              ship: shipData.shipName,
              date: formatDateRangeForItinerary(
                new Date(itineraryData.departureDate),
                new Date(cruiseFinishDate),
                {
                  sameMonthSameYear: itineraryLabels.dateRangeFormatSameMonthSameYear,
                  differentMonthSameYear: itineraryLabels.dateRangeFormatDifferentMonthSameYear,
                  differentYear: itineraryLabels.dateRangeFormatDifferentYear,
                }
              ),
            })}
          </div>
        </div>
        <div className={styles['cmp-itineraryCard-bookingHero__texts-bookingNumberLabel']}>
          {replacePlaceholders(itineraryLabels.bookingNumberLabel, {
            bookingID: bookingData.number,
          })}
        </div>
      </div>
    </div>
  );
};

const BookingDetails = ({
  bookingData,
  itineraryLabels,
  itineraryData,
  shipData,
  isAllMyCruises,
  experiences,
}: {
  bookingData: GetBookingMyMscResponse;
  itineraryLabels: ItineraryCardWrapperProps;
  itineraryData: ItineraryResponse;
  shipData: GetShipsResponse;
  isAllMyCruises: boolean;
  experiences?: IGetExperienceDetailsResponse;
}) => {
  const cruiseFinishDate = useMemo(
    () => addDays(itineraryData.departureDate, itineraryData.numberOfSailingDays),
    [itineraryData.departureDate, itineraryData.numberOfSailingDays]
  );

  const deckNum = isAllMyCruises
    ? ''
    : (() => {
        const cabinNumber = bookingData?.cabins?.[0]?.number;
        if (!cabinNumber || typeof cabinNumber !== 'string') return '';
        if (cabinNumber.length === 4) return cabinNumber.substring(0, 1);
        if (cabinNumber.length === 5) return cabinNumber.substring(0, 2);
        return '';
      })();

  const startingLocation = itineraryData.days?.[0].port.name;
  const startingLocationCode = itineraryData.days?.[0].port.code;

  const finishLocation = itineraryData.days[itineraryData.days.length - 1].port.name;
  const finishLocationCode = itineraryData.days[itineraryData.days.length - 1].port.code;
  const assignedSlot = useSelector((state: RootState) =>
    state.myCruise.slots?.slots?.find((slot) => slot.isAssigned)
  );

  const formatAssignedSlotTime = (time?: string) => (time ? formatTime(time) : '');
  const startTime = assignedSlot && formatAssignedSlotTime(assignedSlot?.startTime);
  const endTime = assignedSlot && formatAssignedSlotTime(assignedSlot?.endTime);
  const composeDetails = (): { label: string; description: string | undefined }[] => {
    const embarkation =
      isAllMyCruises || !(startTime && endTime)
        ? { label: '', description: '' }
        : {
            label: itineraryLabels.embarkationLabel,
            description: itineraryLabels.embarkationTime
              ? `${replacePlaceholders(itineraryLabels.embarkationTime, {
                  startTime: startTime,
                  endTime: endTime,
                })}`
              : '',
          };

    const { cruiseId } = getActiveSession();
    const stateroom = isAllMyCruises
      ? []
      : bookingData.cabins
          .filter((cruiseBooking) => cruiseBooking.cruise.id === cruiseId)
          .map((cruiseBooking: CabinI, index: number) => {
            const categoryKeys = [
              'interiorCategoryCf',
              'oceanViewCategoryCf',
              'balconyCategoryCf',
              'suiteCategoryCf',
              'yachtClubCategoryCf',
            ];

            const cabinCategory = categoryKeys
              .map((key) => shipData[key]) // Get category arrays
              .flat() // Flatten the array (to merge all categories)
              .find((item) => item.cabinCode === cruiseBooking.categoryCode);
            //the object that has the cabinCategoryCode
            const participantNo = isAllMyCruises ? '' : cruiseBooking.passengers?.length;

            let category = '';
            if (cabinCategory) {
              const experienceCf = getBookedExperienceDetail(bookingData, experiences);

              category = cabinCategory.cabinNameShipPage || '';
              if (experienceCf?.experienceTitle) {
                category += `, ${experienceCf.experienceTitle}`;
              }
            }

            //Manage guaranteed stateroom description based on the cabin number
            let roomDescription = '';
            if (cruiseBooking.number === 'G00000') {
              roomDescription = itineraryLabels.guaranteedStateroomDescription
                ? replacePlaceholders(itineraryLabels.guaranteedStateroomDescription, {
                    passengerField: managePlurals(
                      Number(participantNo),
                      itineraryLabels.passengerFieldLabel
                    ),
                    passengerNum: participantNo?.toString(),
                  })
                : '';
            } else {
              roomDescription = itineraryLabels.stateroomDescription
                ? replacePlaceholders(itineraryLabels.stateroomDescription, {
                    passengerField: managePlurals(
                      Number(participantNo),
                      itineraryLabels.passengerFieldLabel
                    ),
                    passengerNum: participantNo?.toString(),
                    deckNum,
                    cabinCategory: category,
                    roomNum: cruiseBooking.number,
                  })
                : '';
            }

            return {
              label: itineraryLabels.stateroomLabel + ' ' + ++index,
              description: roomDescription.split('-').join(' • '),
            };
          });

    const cruiseType = {
      label:
        startingLocationCode === finishLocationCode
          ? itineraryLabels.roundtripLabel
          : itineraryLabels.onewayLabel,
      description: replacePlaceholders(
        startingLocationCode === finishLocationCode
          ? itineraryLabels.roundtripDescription
          : itineraryLabels.onewayDescription,
        {
          startingLocation,
          finishLocation,
          ship: shipData.shipName,
          date: formatDateRangeForItinerary(
            new Date(itineraryData.departureDate),
            new Date(cruiseFinishDate),
            {
              sameMonthSameYear: itineraryLabels.dateRangeFormatSameMonthSameYear,
              differentMonthSameYear: itineraryLabels.dateRangeFormatDifferentMonthSameYear,
              differentYear: itineraryLabels.dateRangeFormatDifferentYear,
            }
          ),
        }
      ),
    };

    const visiting = {
      label: itineraryLabels.visitingLabel,
      description: itineraryData.days.map((segment) => segment.port.name).join(' • '),
    };
    if (isAllMyCruises) {
      return [cruiseType, visiting];
    }
    return [embarkation, ...stateroom, cruiseType, visiting];
  };

  return (
    <div className={styles['cmp-itineraryCard-bookingDetails__container']}>
      {composeDetails().map((detail, index) => {
        return (
          <div key={`${index}-${detail.label}`}>
            <div className={styles['cmp-itineraryCard-bookingDetails__title']}>{detail.label}</div>
            <div className={styles['cmp-itineraryCard-bookingDetails__subtitle']}>
              {detail.description}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BookingStatus = ({ children }: { children: ReactNode }) => {
  return <div className={styles['cmp-itineraryCard-bookingStatus__container']}>{children}</div>;
};

BookingStatus.Balance = function BookingStatusBalance({
  balanceLabel,
  balanceDescription,
  bookingData,
  disableRemainingBalanceB2B,
}: {
  balanceLabel: string;
  balanceDescription?: string;
  bookingData: GetBookingMyMscResponse;
  disableRemainingBalanceB2B?: boolean;
}) {
  const balance =
    Number(bookingData?.totalWithoutPrepaid?.due?.gross) > 0
      ? Number(bookingData?.totalWithoutPrepaid?.due?.gross)
      : Number(bookingData?.total?.due?.gross);
  const date =
    bookingData?.status.code === BookingStatusEnum.OPTIONAL &&
    isB2CBooking(bookingData) &&
    bookingData?.optionExpirationDate
      ? bookingData?.optionExpirationDate
      : bookingData?.totalWithoutPrepaid?.dueDate;
  const balanceDueDate =
    balance > 0 && isB2CBooking(bookingData) && date && dayjs(date).format(resolveDateFormat());
  const hideB2BBalance = !isB2CBooking(bookingData) && disableRemainingBalanceB2B;
  return (
    <div>
      {!hideB2BBalance && (
        <>
          <div className={styles['cmp-itineraryCard-bookingStatus__remainingBalanceLabel']}>
            {balanceLabel}
          </div>
          <div className={styles['cmp-itineraryCard-bookingStatus__remainingBalanceData']}>
            {balance >= 0
              ? getCurrencySymbol() + balance
              : '-' + getCurrencySymbol() + Math.abs(balance)}
          </div>
        </>
      )}
      <div className={styles['cmp-itineraryCard-bookingStatus__remainingBalanceDesc']}>
        <div dangerouslySetInnerHTML={{ __html: balanceDescription ?? '' }}></div>
        <div>{balanceDueDate}</div>
      </div>
    </div>
  );
};

BookingStatus.Button = function BookingStatusButton({
  buttonLabel,
  buttonLink,
  isRouting,
  handleClick,
}: {
  buttonLabel: string;
  buttonLink: string;
  isRouting?: boolean;
  handleClick?: () => void;
}) {
  return !isRouting ? (
    <Button
      buttonLink={{ url: buttonLink }}
      text={buttonLabel}
      onClick={handleClick}
      styleOption={'cmp-button__primary -full-width'}
    />
  ) : (
    <Button
      text={buttonLabel}
      styleOption={'cmp-button__primary -full-width'}
      onClick={handleClick}
    />
  );
};

BookingStatus.Status = function BookingStatusStatus({
  statusLabel,
  bookingData,
}: {
  statusLabel: string;
  bookingData: AllBooking;
}) {
  const statusCode = getBookingStatusConfig()?.find(
    (status: BookingStatusObject) => status?.bookingStatusEnum === bookingData?.status
  )?.bookingStatusLabel;
  return (
    <>
      <div className={styles['cmp-itineraryCard-bookingStatus__statusLabel']}>{statusLabel}</div>
      <div className={styles['cmp-itineraryCard-bookingStatus__status']}>{statusCode}</div>
    </>
  );
};

export default ItineraryCard;
